import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Play, Pause, Square, RotateCcw, Download, CheckCircle, XCircle, Clock, Zap, Info, AlertTriangle, ArrowLeftRight, ShieldCheck } from "lucide-react";

const STATUS_COLORS = {
  success: "#059669", failed: "#DC2626", pending: "#9CA3AF",
  running: "#006080", dry_run: "#7C3AED", paused: "#D97706", stopped: "#6B7280", skipped: "#7C3AED",
};

const InfoTooltip = ({ text }) => (
  <div className="relative inline-block group align-middle ml-1.5 cursor-help">
    <Info size={13} className="text-gray-400 hover:text-[#006080]" />
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

function ValidationModal({ result, onProceed, onCancel }) {
  if (!result) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className={`px-5 py-4 border-b ${result.ready ? "bg-green-50 border-green-100" : "bg-amber-50 border-amber-100"}`}>
          <div className="flex items-center gap-2">
            {result.ready ? <CheckCircle size={18} className="text-green-600" /> : <AlertTriangle size={18} className="text-amber-600" />}
            <h3 className="font-semibold text-gray-900">
              {result.ready ? "Data looks good" : `${result.issue_count} issue${result.issue_count !== 1 ? "s" : ""} found`}
            </h3>
          </div>
          <p className="text-xs text-gray-500 mt-1">{result.total_rows} rows scanned</p>
        </div>
        <div className="p-5 max-h-72 overflow-y-auto space-y-2">
          {result.issues.length === 0 ? (
            <p className="text-sm text-gray-500">No issues detected. Ready to execute.</p>
          ) : result.issues.map((issue, i) => (
            <div key={i} className={`rounded-lg p-3 border text-xs ${
              issue.severity === "error" ? "bg-red-50 border-red-200" :
              issue.severity === "warning" ? "bg-amber-50 border-amber-200" :
              "bg-blue-50 border-blue-200"
            }`}>
              <div className="flex items-start gap-2">
                <span className={`font-bold uppercase text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                  issue.severity === "error" ? "bg-red-100 text-red-700" :
                  issue.severity === "warning" ? "bg-amber-100 text-amber-700" :
                  "bg-blue-100 text-blue-700"
                }`}>{issue.severity}</span>
                <div>
                  <p className="font-semibold text-gray-800">{issue.column || "—"}</p>
                  <p className="text-gray-600 mt-0.5">{issue.message}</p>
                  {issue.examples?.length > 0 && (
                    <p className="text-gray-400 mt-0.5">e.g. {issue.examples.join(", ")}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm"
            className={result.ready ? "bg-[#006080] text-white hover:bg-[#004d66]" : "bg-amber-500 text-white hover:bg-amber-600"}
            onClick={onProceed}
          >{result.ready ? "Run Now" : "Proceed Anyway"}</Button>
        </div>
      </div>
    </div>
  );
}

export default function ExecutionConsolePage() {
  const { executionId, uploadId } = useParams();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [apiDefs, setApiDefs] = useState([]);
  const [form, setForm] = useState({
    upload_id: uploadId || "",
    api_definition_id: "",
    start_row: 0,
    end_row: "",
    concurrency: 1,
    delay_ms: 0,
    dry_run: false,
    dry_run_count: 3,
  });
  const [execution, setExecution] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    client.get("/files/").then(r => setUploads(r.data));
    client.get("/api-defs/").then(r => setApiDefs(r.data));
    if (executionId) loadExecution(executionId);
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (execution && ["running", "paused"].includes(execution.status)) {
      startPolling(execution._id);
    } else {
      stopPolling();
    }
  }, [execution?.status]);

  const loadExecution = async (id) => {
    try {
      const res = await client.get(`/executions/${id}`);
      setExecution(res.data);
      loadLogs(id, 1);
    } catch (err) {}
  };

  const loadLogs = async (id, page) => {
    try {
      const res = await client.get(`/executions/${id}/logs`, { params: { page, per_page: 50 } });
      setLogs(res.data.logs);
      setLogTotal(res.data.total);
      setLogPage(page);
    } catch (err) {}
  };

  const startPolling = (id) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await client.get(`/executions/${id}`);
        setExecution(res.data);
        loadLogs(id, logPage);
        if (["completed", "failed", "stopped"].includes(res.data.status)) {
          stopPolling();
        }
      } catch (err) {}
    }, 1000);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const _doRun = async (overrides = {}) => {
    if (!form.upload_id || !form.api_definition_id) {
      setError("Please select a file and API definition.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const payload = {
        ...form,
        ...overrides,
        start_row: parseInt(form.start_row) || 0,
        end_row: form.end_row !== "" ? parseInt(form.end_row) : null,
        concurrency: parseInt(form.concurrency) || 1,
        delay_ms: parseInt(form.delay_ms) || 0,
        dry_run_count: parseInt(form.dry_run_count) || 3,
      };
      const res = await client.post("/executions/", payload);
      navigate(`/executions/${res.data.execution_id}`);
      setTimeout(() => loadExecution(res.data.execution_id), 500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRun = () => _doRun();

  const handleQuickDryRun = () => _doRun({ dry_run: true, dry_run_count: 3 });

  const handleValidateAndRun = async () => {
    if (!form.upload_id || !form.api_definition_id) {
      setError("Please select a file and API definition first.");
      return;
    }
    setValidating(true); setValidationResult(null); setError("");
    try {
      const res = await client.post("/executions/pre-validate", {
        upload_id: form.upload_id,
        api_definition_id: form.api_definition_id,
      });
      setValidationResult(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setValidating(false); }
  };

  const handleControl = async (action) => {
    if (!execution) return;
    await client.put(`/executions/${execution._id}/control`, { action });
    loadExecution(execution._id);
  };

  const handleRetryFailed = async () => {
    if (!execution) return;
    try {
      const res = await client.post(`/executions/${execution._id}/retry-failed`);
      navigate(`/executions/${res.data.execution_id}`);
      setTimeout(() => loadExecution(res.data.execution_id), 500);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const progress = execution ? (execution.total_rows > 0 ? (execution.processed_count / execution.total_rows) * 100 : 0) : 0;

  const isRunning = execution?.status === "running";
  const isPaused = execution?.status === "paused";
  const isDone = execution && ["completed", "failed", "stopped"].includes(execution.status);

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Execution Console</h1>
          <InfoTooltip text="Run an API call for every row in your uploaded file. Configure file, API definition, row range, concurrency, and optional dry-run mode before firing." />
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <p className="text-gray-500 text-sm">Configure and run your API calls against your data</p>
          <button onClick={() => navigate("/executions/diff")}
            className="text-xs text-[#006080] hover:underline flex items-center gap-1">
            <ArrowLeftRight size={12} /> Compare Runs
          </button>
        </div>
      </div>

      {validationResult && (
        <ValidationModal
          result={validationResult}
          onProceed={() => { setValidationResult(null); _doRun(); }}
          onCancel={() => setValidationResult(null)}
        />
      )}

      {/* Config panel */}
      {!execution && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Execution Settings</p>
          {error && <div className="mb-3 p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Source File *</Label>
              <Select value={form.upload_id} onValueChange={v => setForm(f => ({ ...f, upload_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="exec-upload-select"><SelectValue placeholder="Select file..." /></SelectTrigger>
                <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">API Definition *</Label>
              <Select value={form.api_definition_id} onValueChange={v => setForm(f => ({ ...f, api_definition_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="exec-api-def-select"><SelectValue placeholder="Select API..." /></SelectTrigger>
                <SelectContent>{apiDefs.map(d => <SelectItem key={d._id} value={d._id}>{d.name} ({d.method})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Concurrency</Label>
              <Input className="mt-1" type="number" min={1} max={50} value={form.concurrency} onChange={e => setForm(f => ({ ...f, concurrency: e.target.value }))} data-testid="exec-concurrency" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Start Row (0-based)</Label>
              <Input className="mt-1" type="number" min={0} value={form.start_row} onChange={e => setForm(f => ({ ...f, start_row: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">End Row (blank = all)</Label>
              <Input className="mt-1" type="number" min={0} value={form.end_row} onChange={e => setForm(f => ({ ...f, end_row: e.target.value }))} placeholder="all rows" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Delay Between Requests (ms)</Label>
              <Input className="mt-1" type="number" min={0} value={form.delay_ms} onChange={e => setForm(f => ({ ...f, delay_ms: e.target.value }))} />
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded" checked={form.dry_run} onChange={e => setForm(f => ({ ...f, dry_run: e.target.checked }))} data-testid="dry-run-toggle" />
              <span className="text-sm font-medium text-gray-700">Dry Run Mode</span>
              <InfoTooltip text="Dry run executes without making real API calls. The response is simulated and nothing is sent. Useful for testing your mapping before going live." />
            </label>
            {form.dry_run && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-500">Rows to test:</Label>
                <Input className="w-16 h-7 text-sm" type="number" min={1} value={form.dry_run_count} onChange={e => setForm(f => ({ ...f, dry_run_count: e.target.value }))} />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              className="bg-[#006080] hover:bg-[#004d66] text-white"
              onClick={handleRun}
              disabled={loading}
              data-testid="run-btn"
            >
              <Play size={15} className="mr-2" />
              {form.dry_run ? "Dry Run" : "Run Now"}
            </Button>
            <Button
              variant="outline"
              className="border-[#006080] text-[#006080] hover:bg-[#006080]/5"
              onClick={handleQuickDryRun}
              disabled={loading}
              data-testid="quick-dry-run-btn"
            >
              <Zap size={14} className="mr-1.5" />
              Test 3 Rows
              <InfoTooltip text="Quick dry-run: executes the first 3 rows with dry_run=true so you can verify your mapping is correct before running the full file." />
            </Button>
            <Button
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={handleValidateAndRun}
              disabled={validating || loading}
              data-testid="validate-btn"
            >
              <ShieldCheck size={14} className="mr-1.5" />
              {validating ? "Validating..." : "Validate Data"}
              <InfoTooltip text="Scans all rows for common issues (empty required fields, mismatched types) before running. You'll see a summary and can choose to proceed or fix first." />
            </Button>
          </div>
        </div>
      )}

      {/* Execution status */}
      {execution && (
        <div className="space-y-4">
          {/* Status bar */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge
                  className="text-xs font-semibold"
                  style={{ background: `${STATUS_COLORS[execution.status]}15`, color: STATUS_COLORS[execution.status], border: `1px solid ${STATUS_COLORS[execution.status]}30` }}
                >
                  {execution.status.toUpperCase()}
                </Badge>
                {execution.dry_run && <Badge variant="secondary" className="text-xs">DRY RUN</Badge>}
              </div>
              <div className="flex gap-2">
                {isRunning && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => handleControl("pause")}>
                    <Pause size={12} className="mr-1" /> Pause
                  </Button>
                )}
                {isPaused && (
                  <Button size="sm" className="h-7 px-2 text-xs bg-[#006080] text-white" onClick={() => handleControl("resume")}>
                    <Play size={12} className="mr-1" /> Resume
                  </Button>
                )}
                {(isRunning || isPaused) && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => handleControl("stop")}>
                    <Square size={12} className="mr-1" /> Stop
                  </Button>
                )}
                {isDone && execution.error_count > 0 && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleRetryFailed}>
                    <RotateCcw size={12} className="mr-1" /> Retry Failed
                  </Button>
                )}
                {isDone && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => {
                    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/executions/${execution._id}/export`, "_blank");
                  }}>
                    <Download size={12} className="mr-1" /> Export Log
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setExecution(null); navigate("/execute/" + (uploadId || "")); }}>
                  New Run
                </Button>
              </div>
            </div>
            <Progress value={progress} className="h-2 mb-3" />
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: "Processed", value: execution.processed_count, icon: Clock, color: "#006080" },
                { label: "Success", value: execution.success_count, icon: CheckCircle, color: "#059669" },
                { label: "Failed", value: execution.error_count, icon: XCircle, color: "#DC2626" },
                { label: "Total", value: execution.total_rows, icon: Zap, color: "#D97706" },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="text-center">
                  <div className="text-2xl font-black" style={{ color }}>{value}</div>
                  <div className="text-xs text-gray-400">{label}</div>
                </div>
              ))}
            </div>
            {execution.avg_response_time_ms && (
              <p className="text-xs text-gray-400 mt-2 text-center">Avg response: {Math.round(execution.avg_response_time_ms)}ms</p>
            )}
          </div>

          {/* Log table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Execution Log ({logTotal} entries)</p>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full data-table text-xs" data-testid="execution-log-table">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2">Row #</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">HTTP</th>
                    <th className="px-3 py-2">Time (ms)</th>
                    <th className="px-3 py-2 max-w-[200px]">Extracted Values</th>
                    <th className="px-3 py-2 max-w-[200px]">Error / Response</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} data-testid={`log-row-${log.row_index}`}>
                      <td className="px-3 py-2 font-mono">{log.row_index + 1}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: `${STATUS_COLORS[log.status]}15`, color: STATUS_COLORS[log.status] }}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono">{log.http_status || "-"}</td>
                      <td className="px-3 py-2 font-mono">{log.response_time_ms ?? "-"}</td>
                      <td className="px-3 py-2 max-w-[200px]">
                        {log.extracted_values && Object.keys(log.extracted_values).length > 0 ? (
                          <div className="space-y-0.5">
                            {Object.entries(log.extracted_values).map(([k, v]) => (
                              <div key={k} className="flex items-center gap-1 text-[10px]">
                                <span className="font-mono text-amber-700 bg-amber-50 px-1 rounded">{k}</span>
                                <span className="text-gray-500 truncate max-w-[80px]">{v || "—"}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 truncate max-w-[200px] text-gray-400">{log.error || (log.response_body ? log.response_body.slice(0, 60) : "-")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {logTotal > 50 && (
              <div className="px-4 py-2 border-t border-gray-100 flex gap-2">
                <Button size="sm" variant="outline" className="h-6 text-xs" disabled={logPage <= 1} onClick={() => loadLogs(execution._id, logPage - 1)}>Prev</Button>
                <span className="text-xs text-gray-400 self-center">{logPage} / {Math.ceil(logTotal / 50)}</span>
                <Button size="sm" variant="outline" className="h-6 text-xs" disabled={logPage >= Math.ceil(logTotal / 50)} onClick={() => loadLogs(execution._id, logPage + 1)}>Next</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
